import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ShoppingBag, Ticket, Euro, CalendarCheck, Store, QrCode, Crown, Sparkles, BarChart3, Shield, Cake, FileSignature, Wallet, Link2, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { AIMarketingTools } from '../components/AIMarketingTools';
import { BusinessReservationsSection } from '../components/BusinessReservationsSection';
import { TPVConfigSection } from '../components/TPVConfigSection';
import { QRAnalyticsDashboard } from '../components/QRAnalyticsDashboard';
import { SubscriptionCheckout } from '../components/SubscriptionCheckout';
import { UserCounter } from '../components/UserCounter';
import { PushNotificationToggle } from '../components/PushNotificationToggle';
import { TicketAnalyticsDashboard } from '../components/TicketAnalyticsDashboard';
import { FlyerDownloadSection } from '../components/FlyerDownloadSection';
import { MonetizationStore } from '../components/MonetizationStore';
import { AnalyticsDashboard } from '../components/AnalyticsDashboard';
import { BusinessProfileEditor } from '../components/BusinessProfileEditor';
import { DataPrivacySection } from '../components/DataPrivacySection';
import { BirthdayClubDashboard } from '../components/BirthdayClubDashboard';
import { LegalAgreementsSection } from '../components/LegalAgreementsSection';
import { BusinessFinanceDashboard } from '../components/BusinessFinanceDashboard';
import { BusinessLandingsSection } from '../components/BusinessLandingsSection';
import { ReactivationDashboard } from '../components/ReactivationDashboard';
import { InlineAdBanner } from '../components/AdComponents';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BusinessDashboard = () => {
  const { t, i18n } = useTranslation();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [kupons, setKupons] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'subscription', 'analytics'

  useEffect(() => {
    fetchStats();
    fetchSubscription();
    fetchKupons();
    
    // Check subscription status from URL
    if (searchParams.get('subscription') === 'success') {
      toast.success('¡Suscripción activada correctamente!');
      fetchSubscription();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (searchParams.get('subscription') === 'cancelled') {
      toast.info('Suscripción cancelada');
    }
  }, [searchParams]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/business/${user.id}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/current`, {
        headers: { 'X-Business-Id': user?.id }
      });
      setSubscription(response.data);
    } catch (error) {
      console.log('No subscription info:', error);
      setSubscription({ plan_id: 'free', kupons_remaining: 1 });
    }
  };

  const fetchKupons = async () => {
    try {
      const response = await axios.get(`${API}/kupons/business/${user?.id}`);
      setKupons(response.data || []);
    } catch (error) {
      console.error('Error fetching kupons:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-local-sage" />
      </div>
    );
  }

  const statCards = [
    {
      icon: <Euro className="w-8 h-8" />,
      label: t('business_dashboard.total_sales'),
      value: `${stats?.totalSales || 0}€`,
      color: 'bg-local-sage'
    },
    {
      icon: <ShoppingBag className="w-8 h-8" />,
      label: t('business_dashboard.total_orders'),
      value: stats?.totalOrders || 0,
      color: 'bg-blue-500'
    },
    {
      icon: <Ticket className="w-8 h-8" />,
      label: t('business_dashboard.active_kupons'),
      value: stats?.activeKupons || 0,
      color: 'bg-reward-gold'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      label: t('business_dashboard.avg_per_order'),
      value: stats?.totalOrders > 0 ? `${(stats.totalSales / stats.totalOrders).toFixed(2)}€` : '0€',
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="min-h-screen py-4 sm:py-8">
      <div className="container mx-auto px-4 sm:px-6 max-w-full overflow-x-hidden">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-manrope font-bold text-3xl sm:text-4xl mb-2">
            {t('business_dashboard.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('business_dashboard.welcome')} {user?.businessName || user?.name}
          </p>
          
          {/* Community Progress Counter */}
          <div className="mt-4">
            <UserCounter variant="progress" />
          </div>
          
          {/* Sponsored Content - Minimal inline ad */}
          <div className="mt-4">
            <InlineAdBanner />
          </div>
          
          {/* Push Notifications Toggle */}
          <div className="mt-4">
            <PushNotificationToggle userId={user?.id} userType="business" />
          </div>
        </motion.div>

        {/* Subscription & Tab Navigation */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          {/* Current Plan Info */}
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              subscription?.plan_id === 'business' ? 'bg-purple-500 text-white' :
              subscription?.plan_id === 'pro' ? 'bg-emerald-500 text-white' :
              subscription?.plan_id === 'starter' ? 'bg-blue-500 text-white' :
              'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
            }`}>
              <Crown className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('business_dashboard.your_plan')}</p>
              <p className="font-bold capitalize">{subscription?.plan_id || 'Free'}</p>
            </div>
            {subscription && (
              <div className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">
                <span className="text-sm">
                  {subscription.kupons_remaining === -1 || subscription.kupons_limit === -1
                    ? t('business_dashboard.unlimited_kupons')
                    : `${subscription.kupons_remaining || 0}/${subscription.kupons_limit || subscription.kupons_per_month || 1} ${t('business_dashboard.kupons')}`}
                </span>
              </div>
            )}
          </div>

          {/* Tab Navigation - Scrollable on mobile */}
          <div className="w-full md:w-auto overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1 w-max md:flex-wrap md:w-auto">
              {[
                { id: 'overview', label: { es: 'Resumen', en: 'Overview', fr: 'Aperçu' }, icon: TrendingUp },
                { id: 'finance', label: { es: 'Finanzas', en: 'Finance', fr: 'Finances' }, icon: Wallet },
                { id: 'reactivation', label: { es: 'Reactivación IA', en: 'AI Reactivation', fr: 'Réactivation IA' }, icon: Zap },
                { id: 'landings', label: { es: 'Landings', en: 'Landings', fr: 'Landings' }, icon: Link2 },
                { id: 'profile', label: { es: 'Perfil', en: 'Profile', fr: 'Profil' }, icon: Store },
                { id: 'birthday-club', label: { es: 'Cumpleaños', en: 'Birthday', fr: 'Anniversaire' }, icon: Cake },
                { id: 'analytics-pro', label: { es: 'Analíticas', en: 'Analytics', fr: 'Analytique' }, icon: BarChart3 },
                { id: 'subscription', label: { es: 'Plan', en: 'Plan', fr: 'Plan' }, icon: Crown },
                { id: 'legal', label: { es: 'Legal', en: 'Legal', fr: 'Légal' }, icon: FileSignature },
                { id: 'analytics', label: { es: 'QR', en: 'QR', fr: 'QR' }, icon: QrCode },
                { id: 'privacy', label: { es: 'RGPD', en: 'GDPR', fr: 'RGPD' }, icon: Shield }
              ].map(tab => {
                const lang = i18n.language?.substring(0, 2) || 'es';
                const label = tab.label[lang] || tab.label.es;
                return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id 
                      ? 'bg-white dark:bg-gray-700 shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              );
              })}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
              data-testid={`stat-card-${idx}`}
            >
              <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center text-white mb-4`}>
                {stat.icon}
              </div>
              <p className="text-sm text-muted-foreground mb-1">{stat.label}</p>
              <p className="text-2xl font-bold">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* AI Marketing Tools */}
        <AIMarketingTools businessId={user?.id} />

        {/* Monetization Store - Premium Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-yellow-500" />
            <h2 className="font-manrope font-bold text-2xl">{t('business_dashboard.power_up')}</h2>
          </div>
          <MonetizationStore businessId={user?.id} onPurchaseComplete={() => fetchStats()} />
        </motion.div>

        {/* Flyer Download Section - Promotional Materials */}
        <FlyerDownloadSection businessId={user?.id} />

        {/* Reservations Section - NEW */}
        <BusinessReservationsSection businessId={user?.id} />

        {/* TPV Configuration - NEW */}
        <TPVConfigSection businessId={user?.id} />

        {/* Ticket Verification Analytics */}
        <TicketAnalyticsDashboard businessId={user?.id} />

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="font-manrope font-bold text-2xl mb-6">{t('business_dashboard.recent_orders')}</h2>
          
          {stats?.recentOrders && stats.recentOrders.length > 0 ? (
            <div className="space-y-4" data-testid="recent-orders">
              {stats.recentOrders.map((order) => (
                <div
                  key={order.id}
                  className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{order.kuponTitle}</h3>
                      <p className="text-sm text-muted-foreground mb-1">
                        {t('business_dashboard.customer')} {order.customerName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Email: {order.customerEmail}
                      </p>
                      {order.customerPhone && (
                        <p className="text-sm text-muted-foreground">
                          {t('business_dashboard.phone')} {order.customerPhone}
                        </p>
                      )}
                      {order.pickupTime && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {t('business_dashboard.pickup')} {new Date(order.pickupTime).toLocaleString(i18n.language?.startsWith('es') ? 'es-ES' : i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US')}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-local-sage mb-2">
                        {order.amount.toFixed(2)}€
                      </p>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        order.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                        order.status === 'ready' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100' :
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100'
                      }`}>
                        {order.status === 'completed' ? t('business_dashboard.completed') :
                         order.status === 'ready' ? t('business_dashboard.ready') :
                         t('business_dashboard.pending')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-card rounded-2xl">
              <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('business_dashboard.no_orders')}</p>
            </div>
          )}
        </motion.div>
          </>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h2 className="font-manrope font-bold text-2xl mb-6">{t('business_dashboard.manage_subscription')}</h2>
            <SubscriptionCheckout 
              businessId={user?.id} 
              currentPlan={subscription?.plan_id || 'free'} 
            />
          </motion.div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <BusinessProfileEditor 
              user={user} 
              onUpdate={(updatedUser) => {
                // Refresh page data
                window.location.reload();
              }}
            />
          </motion.div>
        )}

        {/* Birthday Club Tab */}
        {activeTab === 'birthday-club' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <BirthdayClubDashboard user={user} />
          </motion.div>
        )}

        {/* Analytics Pro Tab */}
        {activeTab === 'analytics-pro' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <AnalyticsDashboard 
              businessId={user?.id} 
              plan={user?.analytics_subscription?.plan || 'basic'}
              onUpgrade={() => setActiveTab('subscription')}
            />
          </motion.div>
        )}

        {/* QR Analytics Tab */}
        {activeTab === 'analytics' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <QRAnalyticsDashboard businessId={user?.id} />
          </motion.div>
        )}

        {/* Privacy/Data Management Tab */}
        {activeTab === 'privacy' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <DataPrivacySection userId={user?.id} userType="business" />
          </motion.div>
        )}

        {/* Legal Agreements Tab */}
        {activeTab === 'legal' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <LegalAgreementsSection user={user} />
          </motion.div>
        )}

        {/* Finance Dashboard Tab */}
        {activeTab === 'finance' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <BusinessFinanceDashboard user={user} />
          </motion.div>
        )}

        {/* Landings & Appointments Tab */}
        {activeTab === 'landings' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <BusinessLandingsSection user={user} />
          </motion.div>
        )}

        {/* Reactivation AI Tab */}
        {activeTab === 'reactivation' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <ReactivationDashboard businessId={user?.id} kupons={kupons} />
          </motion.div>
        )}
      </div>
    </div>
  );
};
